import {
  PITCH_CONFIDENCE_MIN,
  createPitchTrackerConfig,
  effectiveEventIntervalMs,
} from '@muzly/shared';
import type { NativeF0Frame } from '@muzly/shared';

import type { MelodyNote } from './melody';

/**
 * songScoring — 레퍼런스 멜로디 대비 **채점**(음정 정확도 · 구간 경향 · 박자)의 순수 함수.
 *
 * 여기가 `pitchAccuracy`를 "가장 가까운 반음 대비"에서 "정답 음 대비"로 바꾸는 자리다
 * (metricGrades의 proxy → solid 승격 근거). 숫자만 내고 문구·표시는 소비처 몫이다.
 *
 * **시간 기준(anchor)**: 멜로디의 `startMs`는 음원 t=0 기준이고, 프레임의 `timestampMs`는
 * **캡처 타임라인** 기준이다(f0frame-contract §1 — 세션 시작이 0인 자기 시계, 벽시계가
 * 아니다). 둘을 맞출 수 있는 유일한 지점은 "레퍼런스 재생을 시작한 순간이 캡처 타임라인의
 * 몇 ms였나"뿐이라 그 값을 `referenceStartMs`로 받는다. 화면은 녹음이 시작되자마자 재생을
 * 걸고(첫 프레임과 같은 순간), 안 넘기면 첫 프레임 시각을 그 자리로 쓴다. 남는 오차는
 * 프레임 간격(≈21ms)과 **보정하지 않은 오디오 출력 지연**이다 — 박자 판정 문턱(±120ms)을
 * 그보다 훨씬 크게 잡은 이유가 이것이다.
 *
 * **모노포닉 가정**: 사람은 한 번에 한 음만 낸다. 시간이 겹치는 음이 실린 차트를 그대로
 * 채점하면 아무리 잘 불러도 겹친 음 중 하나는 반드시 틀린 음이 되므로, 겹치는 음은 **먼저
 * 시작한 것만** 본다(`monophonic`). 겹칠 일이 없는 레퍼런스에서는 이 규칙이 아무것도 하지
 * 않는다 — 지금 실린 자리표시자 차트에만 걸린다.
 *
 * **알고 도는 한계**: 레퍼런스는 스피커로 나가고 마이크는 그걸 같이 듣는다. 에코 제거가
 * 보장되지 않는 기기에서 레퍼런스 음이 f0로 잡히면 "부른 것"으로 채점된다 — 기기 특성이라
 * 여기서 막을 수 없다.
 *
 * React·오디오 IO 의존 없음 — vitest 대상.
 */

/**
 * 점수가 0이 되는 편차(cent). 100cent = 한 반음 = **다른 음을 부른 것**이라, 그 위로는
 * 더 틀렸다고 세분할 의미가 없다(옥타브 위를 불러도 "틀렸다" 하나다).
 */
const MAX_ERROR_CENTS = 100;

/**
 * "맞게 불렀다"고 볼 폭(cent). 반음의 절반 — 이 안이면 사람이 듣기에도 그 음이다.
 * 점수(연속)와 판정(이분)을 **다른 상수로** 나눠 둔다: 50cent 플랫이 "정확"으로 세어지면서
 * 점수는 50점인 게 정상이다(맞는 음이지만 잘 맞진 않았다).
 */
export const ACCURATE_CENTS = 50;

/**
 * 구간 경향(플랫/샾)을 말하기 시작하는 평균 편차(cent). 20cent 아래는 사람이 한 음만
 * 듣고 방향을 집어내기 어려운 폭이라, 그 안이면 "치우침 없음"이라고 말한다.
 */
const TENDENCY_CENTS = 20;

/** 음 시작 직후 전이(포르타멘토·자음)를 빼는 구간(ms). 짧은 음에서는 길이에 비례해 줄인다. */
const NOTE_SETTLE_MS = 30;

/** 이보다 적은 프레임으로는 그 음을 "불렀다"고 보지 않는다 — 스치듯 지난 값은 음정이 아니다. */
const MIN_NOTE_FRAMES = 2;

/** 구간 경향을 말하려면 필요한 최소 음 수. 한두 음의 편차는 그 사람의 경향이 아니다. */
const MIN_SEGMENT_NOTES = 3;

/**
 * 새 음의 시작으로 볼 음정 도약(cent). 온셋을 **멜로디와 무관하게**(부른 소리만 보고)
 * 잡는 이유: 멜로디 음 근처에서 맞는 음정을 찾는 방식이면 박자 지표가 음정 지표의 그림자가
 * 되어, 전체가 반음 낮게 불린 녹음에서 "박자도 못 맞췄다"가 된다. 80cent는 반음보다 살짝
 * 좁게 — 스케일의 이웃 음(100~200cent)은 잡고 비브라토(±50cent 안쪽)는 안 잡는 선이다.
 */
const ONSET_JUMP_CENTS = 80;

/**
 * 검출한 온셋이 어느 음을 겨눴는지 볼 시간 창(ms). 이보다 멀면 그 음을 부른 게 아니다.
 * 창 안에 후보가 여럿이면 시간이 아니라 **음정**으로 고른다(`onsetTarget`) — 시간으로 고르면
 * 촘촘한 멜로디에서 통째로 늦은 사람의 지연이 이웃 음에 흡수돼 씻긴다.
 */
const ONSET_MATCH_MS = 400;

/**
 * "제때 들어갔다"의 폭(ms). 합주에서 ±100ms 안쪽의 어긋남은 잘 지적되지 않고, 우리 앵커
 * 오차(출력 지연 미보정)가 그 안에 들어간다 — 문턱이 앵커 오차보다 작으면 재는 게 사용자
 * 박자가 아니라 기기 지연이 된다.
 */
const ON_TIME_MS = 120;

/** 이만큼 못 맞추면 박자는 **측정 불가**다 — 두세 음으로 낸 평균을 박자라 부르지 않는다. */
const MIN_TIMING_NOTES = 4;

/**
 * 이 이하로 겹친 음은 **동시 발성이 아니라 반올림**이다(모노포닉 축약의 허용치).
 * 음원에서 자동 추출한 차트는 음 경계를 ms로 반올림해 앞 음이 1~2ms 물릴 수 있는데, 그걸
 * 겹침으로 보면 멀쩡한 음이 통째로 채점에서 빠진다. 프레임 간격(≈21ms)보다 짧은 겹침은
 * 어차피 우리가 볼 수 있는 해상도 아래다.
 */
const OVERLAP_TOLERANCE_MS = effectiveEventIntervalMs(
  createPitchTrackerConfig(),
);

export type PitchTendency = 'flat' | 'sharp' | 'onPitch';

export interface SongSegmentScore {
  /** 이 구간에서 실제로 부른 음 수. */
  sungNotes: number;
  totalNotes: number;
  /** 평균 cent 편차(+ 샾 / − 플랫). 부른 음이 모자라면 undefined = **측정 불가**. */
  centDev?: number;
  tendency?: PitchTendency;
}

export interface SongTiming {
  /** 음 시작이 레퍼런스보다 평균 몇 ms 어긋났나(+ 늦음 / − 빠름). */
  meanOffsetMs: number;
  /** 제때(±ON_TIME_MS) 들어간 비율(0..1) — 맞춘 음 기준. */
  onTimeRatio: number;
  matchedNotes: number;
}

export interface SongScore {
  /** 0..100. 부른 음의 평균 점수다(안 부른 음은 분모에서 빠진다). */
  accuracy: number;
  /** 맞게 부른 음(편차 ≤ ACCURATE_CENTS) 비율(0..1) — 부른 음 기준. */
  hitRatio: number;
  sungNotes: number;
  /** 채점 대상 음 수 = **부를 수 있는 줄**의 길이(겹치는 음은 빠진다 — 모노포닉 가정). */
  totalNotes: number;
  /** 멜로디의 아래 절반 / 위 절반. 덱의 "저음 구간은 정확한데 고음에서 플랫" 문장의 근거. */
  low: SongSegmentScore;
  high: SongSegmentScore;
  /** 온셋을 충분히 못 맞추면 undefined = **측정 불가**. */
  timing?: SongTiming;
}

export interface SongScoreInput {
  frames: NativeF0Frame[];
  melody: MelodyNote[];
  /**
   * 레퍼런스 t=0이 캡처 타임라인의 몇 ms였나. 생략하면 **첫 프레임 시각** — 녹음 시작과
   * 동시에 재생을 거는 화면 배선의 값이다(모듈 주석의 anchor 절).
   */
  referenceStartMs?: number;
}

/** 유성 & 신뢰도 충분 & f0 유효 — capture/framesToClips와 같은 선, 같은 출처. */
const isConfidentVoiced = (f: NativeF0Frame): boolean =>
  f.voiced && f.f0Hz != null && f.confidence >= PITCH_CONFIDENCE_MIN;

/** A4=440 기준, 그 음에서 얼마나 벗어났나(cent, + 샾 / − 플랫). */
const centsFromMidi = (hz: number, midi: number): number =>
  1200 * Math.log2(hz / 440) - (midi - 69) * 100;

const median = (values: number[]): number => {
  const sorted = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

const mean = (values: number[]): number =>
  values.reduce((sum, v) => sum + v, 0) / values.length;

/** 레퍼런스 시계(음원 t=0 기준 ms)로 옮긴 유효 프레임. */
type Sung = { atMs: number; hz: number };

const toSung = (frames: NativeF0Frame[], startMs: number): Sung[] =>
  frames
    .filter(isConfidentVoiced)
    .map((f) => ({ atMs: f.timestampMs - startMs, hz: f.f0Hz as number }));

/**
 * 한 음의 편차(cent, 중앙값). 프레임이 모자라면 undefined = **안 불렀다**.
 * 중앙값을 쓰는 이유: 음 끝의 흘러내림이나 튀는 프레임 하나가 평균을 통째로 끌고 간다.
 */
const noteDeviation = (sung: Sung[], note: MelodyNote): number | undefined => {
  // 짧은 음에서 30ms를 통째로 빼면 볼 게 남지 않는다 — 길이의 1/3까지만 자른다.
  const settle = Math.min(NOTE_SETTLE_MS, note.durMs / 3);
  const from = note.startMs + settle;
  const to = note.startMs + note.durMs;
  const cents = sung
    .filter((s) => s.atMs >= from && s.atMs <= to)
    .map((s) => centsFromMidi(s.hz, note.midi));
  return cents.length >= MIN_NOTE_FRAMES ? median(cents) : undefined;
};

/** 편차 → 0..1 점수. 반음 밖은 전부 0(다른 음을 부른 것). */
const noteScore = (centDev: number): number =>
  Math.max(0, 1 - Math.abs(centDev) / MAX_ERROR_CENTS);

const tendencyOf = (centDev: number): PitchTendency => {
  if (centDev <= -TENDENCY_CENTS) {
    return 'flat';
  }
  return centDev >= TENDENCY_CENTS ? 'sharp' : 'onPitch';
};

const segmentScore = (deviations: (number | undefined)[]): SongSegmentScore => {
  const sung = deviations.filter((d): d is number => d != null);
  if (sung.length < MIN_SEGMENT_NOTES) {
    // 부른 음이 모자라면 방향을 말하지 않는다 — "치우침 없음"과 "볼 게 없음"은 다르다.
    return { sungNotes: sung.length, totalNotes: deviations.length };
  }
  const centDev = mean(sung);
  return {
    sungNotes: sung.length,
    totalNotes: deviations.length,
    centDev,
    tendency: tendencyOf(centDev),
  };
};

/** 음이 새로 시작한 자리 — 시각과 그때 낸 음높이. */
type Onset = Sung;

/**
 * 부른 소리에서 **음이 새로 시작한 시각**(레퍼런스 시계 ms). 유성 구간의 시작이거나,
 * 이웃 프레임 사이에서 음정이 크게 꺾인 자리다. 검출 자체는 멜로디를 보지 않는다
 * (위 상수 주석) — 어느 음을 겨눈 것인지는 뒤에서 따로 고른다.
 */
const detectOnsets = (frames: NativeF0Frame[], startMs: number): Onset[] => {
  const onsets: Onset[] = [];
  let prev: Sung | null = null;
  for (const frame of frames) {
    if (!isConfidentVoiced(frame)) {
      prev = null;
      continue;
    }
    const cur = { atMs: frame.timestampMs - startMs, hz: frame.f0Hz as number };
    const jumped =
      prev != null &&
      Math.abs(1200 * Math.log2(cur.hz / prev.hz)) >= ONSET_JUMP_CENTS;
    if (prev == null || jumped) {
      onsets.push(cur);
    }
    prev = cur;
  }
  return onsets;
};

/**
 * 이 온셋이 어느 음을 겨눈 것인가 → 그 음과의 시차(ms). 시간 창 안의 후보 중 **음정이 가장
 * 가까운** 음을 고른다.
 *
 * 시간만 보고 가장 가까운 음에 붙이면 촘촘한 멜로디에서 통째로 늦은 사람이 "다음 음을 일찍
 * 냈다"로 읽혀 지연이 씻긴다(자리표시자 차트로 실측: +150ms가 −16ms로 보고됐다). 음정으로
 * 고르는 건 정확도 지표를 되먹이지 않는다 — 후보 **사이의 순위**만 쓰기 때문에 전체가 반음
 * 낮게 불려도(모든 후보가 같은 만큼 밀린다) 고르는 음이 바뀌지 않는다.
 */
const onsetTarget = (
  onset: Onset,
  melody: MelodyNote[],
): { index: number; offsetMs: number } | null => {
  let index = -1;
  let offsetMs = 0;
  let missCents = Number.POSITIVE_INFINITY;
  for (let i = 0; i < melody.length; i += 1) {
    const dt = onset.atMs - melody[i].startMs;
    if (Math.abs(dt) > ONSET_MATCH_MS) {
      continue;
    }
    const miss = Math.abs(centsFromMidi(onset.hz, melody[i].midi));
    if (miss < missCents) {
      index = i;
      offsetMs = dt;
      missCents = miss;
    }
  }
  return index < 0 ? null : { index, offsetMs };
};

/** 음마다 시차 하나 — 같은 음을 여러 온셋이 겨눴으면 가장 가까운 것만 센다. */
const noteOffsets = (onsets: Onset[], melody: MelodyNote[]): number[] => {
  const byNote = new Map<number, number>();
  for (const onset of onsets) {
    const target = onsetTarget(onset, melody);
    if (target == null) {
      continue;
    }
    const kept = byNote.get(target.index);
    if (kept == null || Math.abs(target.offsetMs) < Math.abs(kept)) {
      byNote.set(target.index, target.offsetMs);
    }
  }
  return [...byNote.values()];
};

const timingOf = (
  frames: NativeF0Frame[],
  melody: MelodyNote[],
  startMs: number,
): SongTiming | undefined => {
  const offsets = noteOffsets(detectOnsets(frames, startMs), melody);
  if (offsets.length < MIN_TIMING_NOTES) {
    return undefined;
  }
  return {
    meanOffsetMs: mean(offsets),
    onTimeRatio:
      offsets.filter((o) => Math.abs(o) <= ON_TIME_MS).length / offsets.length,
    matchedNotes: offsets.length,
  };
};

/** 멜로디를 아래 절반 / 위 절반으로 가르는 음높이(midi) — 중앙값. */
const splitMidi = (melody: MelodyNote[]): number =>
  median(melody.map((n) => n.midi));

/**
 * 사람이 실제로 부를 수 있는 줄로 만든다 — 시간이 겹치는 음은 **먼저 시작한 것만** 남긴다
 * (모듈 주석의 모노포닉 가정). 겹치는 음이 없는 레퍼런스에서는 입력을 그대로 돌려준다.
 */
const monophonic = (melody: MelodyNote[]): MelodyNote[] => {
  const ordered = melody.slice().sort((a, b) => a.startMs - b.startMs);
  const kept: MelodyNote[] = [];
  for (const note of ordered) {
    const prev = kept[kept.length - 1];
    const prevEnd = prev == null ? -Infinity : prev.startMs + prev.durMs;
    if (note.startMs >= prevEnd - OVERLAP_TOLERANCE_MS) {
      kept.push(note);
    }
  }
  return kept;
};

/**
 * 레퍼런스 대비 채점. **`undefined`는 측정 불가**다(0점이 아니다) — 무음·무입력이거나
 * 멜로디가 비었을 때. 0점은 "부르긴 했는데 전부 반음 넘게 틀렸다"라는 다른 사실이다.
 */
export const scoreSong = ({
  frames,
  melody,
  referenceStartMs,
}: SongScoreInput): SongScore | undefined => {
  // 채점 대상은 **부를 수 있는 줄**이다 — `totalNotes`도 이 수다(차트의 음 수가 아니라).
  const notes = monophonic(melody);
  if (notes.length === 0) {
    return undefined;
  }
  const anchorMs = referenceStartMs ?? frames[0]?.timestampMs ?? 0;
  const sung = toSung(frames, anchorMs);
  const deviations = notes.map((note) => noteDeviation(sung, note));
  const scored = deviations.filter((d): d is number => d != null);
  if (scored.length === 0) {
    return undefined;
  }

  const split = splitMidi(notes);
  const pick = (keep: (midi: number) => boolean): (number | undefined)[] =>
    deviations.filter((_, i) => keep(notes[i].midi));

  return {
    accuracy: mean(scored.map(noteScore)) * 100,
    hitRatio:
      scored.filter((d) => Math.abs(d) <= ACCURATE_CENTS).length /
      scored.length,
    sungNotes: scored.length,
    totalNotes: notes.length,
    low: segmentScore(pick((midi) => midi < split)),
    high: segmentScore(pick((midi) => midi >= split)),
    timing: timingOf(frames, notes, anchorMs),
  };
};
